import {
  Table,
  Tooltip,
  TooltipTrigger,
  cx,
} from "@light-technology/components";
import type { ReactNode } from "react";
import type { PlanValues } from "./common";

type PlanValueField<T> = {
  [K in keyof PlanValues]: PlanValues[K] extends T | undefined ? K : never;
}[keyof PlanValues];

type PlanComparisonRowProps<T extends string | number | boolean> = {
  className?: string;
  fieldName: string;
  display: (value: T) => string;
  findMax?: boolean;
  noHighlight?: boolean;
  tooltip?: string;
  valueTooltip?: (value: T, planValues?: PlanValues) => ReactNode;
  unavailableString?: string;
} & (
  | { computedValues: (T | undefined)[]; values?: never; field?: never }
  | { computedValues?: never; values: PlanValues[]; field: PlanValueField<T> }
);

export function PlanComparisonRow<T extends string | number | boolean>({
  className,
  fieldName,
  values,
  field,
  computedValues,
  display,
  findMax,
  noHighlight,
  tooltip,
  valueTooltip,
  unavailableString,
}: PlanComparisonRowProps<T>) {
  const cmpValues: (T | undefined)[] =
    computedValues ?? values.map((v) => v[field] as T | undefined);
  const foundValues = cmpValues.filter((v): v is T => v !== undefined);
  let targetValue: T | undefined;
  if (foundValues.length > 0 && typeof foundValues[0] === "number") {
    const numericValues = foundValues as number[];
    targetValue = (
      findMax ? Math.max(...numericValues) : Math.min(...numericValues)
    ) as T;
  } else if (
    typeof foundValues[0] === "boolean" &&
    foundValues.filter((v) => v === true).length === 1
  ) {
    // For boolean rows, highlight a "true" only when it's a unique advantage.
    targetValue = true as T;
  }
  let allMatches = true;
  for (const foundValue of foundValues) {
    if (targetValue !== foundValue) {
      allMatches = false;
      break;
    }
  }

  const valueTooltips: ReactNode[] = cmpValues.map((value, i) =>
    value !== undefined ? valueTooltip?.(value, values?.[i]) : undefined,
  );

  return (
    <Table.Row className={className}>
      <Table.Cell className="w-[var(--col-w)] min-w-28 text-primary">
        {tooltip ? (
          <Tooltip title={tooltip} arrow>
            <TooltipTrigger className="underline decoration-dotted">
              {fieldName}
            </TooltipTrigger>
          </Tooltip>
        ) : (
          <span>{fieldName}</span>
        )}
      </Table.Cell>
      {cmpValues.map((value, i) => (
        <Table.Cell
          key={i}
          className="w-[var(--col-w)] min-w-28 text-secondary"
        >
          <div className="flex justify-center">
            <div
              className={cx(
                "px-2 py-1 font-mono text-sm whitespace-nowrap",
                noHighlight || allMatches || targetValue === undefined
                  ? ""
                  : value === targetValue
                    ? "rounded-xs bg-utility-success-50"
                    : "",
              )}
            >
              {value === undefined ? (
                (unavailableString ?? "-")
              ) : valueTooltips[i] ? (
                <Tooltip title={valueTooltips[i]} arrow>
                  <TooltipTrigger className="underline decoration-dotted">
                    {display(value)}
                  </TooltipTrigger>
                </Tooltip>
              ) : (
                display(value)
              )}
            </div>
          </div>
        </Table.Cell>
      ))}
    </Table.Row>
  );
}
